import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";

import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useEmpresa } from "../../contexts/EmpresaContext";
import { nivelUsuario } from "../../utils/permissoes";
import { NIVEIS } from "../../config/niveis";
import LayoutDesktop from "../../layouts/LayoutDesktop";
import { db } from "../../config/firebase";

const COLLECTION_CARTOES = "financeiro_cartoes_credito";
const STATUS_OPTIONS = ["ATIVO", "INATIVO"];

function toNumber(value) {
  const n = Number(String(value ?? "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

function formatMoney(value) {
  const n = Number(value || 0);
  return n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function FinanceiroCartoesCreditoFormPage() {
  const { tema } = useTheme();
  const { usuario } = useAuth();
  const { empresaAtual, modoGlobal } = useEmpresa();
  const navigate = useNavigate();
  const { id } = useParams();

  const [clientes, setClientes] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");

  const [form, setForm] = useState({
    empresaId: "",
    nomeCartao: "",
    bandeira: "",
    bancoEmissor: "",
    finalCartao: "",
    limiteTotal: "",
    limiteDisponivel: "",
    diaFechamento: "",
    diaVencimento: "",
    contaPagamento: "",
    status: "ATIVO",
  });

  const isEdicao = !!id;

  const restringirEmpresa = useMemo(() => {
    const nivel = nivelUsuario(usuario);
    return !modoGlobal || nivel < NIVEIS.TECNICO_SENIOR;
  }, [modoGlobal, usuario]);

  const empresaEscopoId = empresaAtual?.id || usuario?.empresaId || "";

  // Carregar empresas
  useEffect(() => {
    const carregarClientes = async () => {
      if (!usuario?.grupoId) return;
      try {
        const snap = await getDocs(
          query(collection(db, "empresas"), where("grupoId", "==", usuario.grupoId))
        );
        const lista = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR"));
        setClientes(lista);
      } catch (e) {
        console.error("Erro ao carregar empresas:", e);
      }
    };

    carregarClientes();
  }, [usuario?.grupoId]);

  // Atualizar empresaId do form se restringir
  useEffect(() => {
    if (restringirEmpresa) {
      setForm((prev) => ({ ...prev, empresaId: empresaEscopoId || "" }));
    }
  }, [restringirEmpresa, empresaEscopoId]);

  // Carregar dados se edição
  useEffect(() => {
    if (!isEdicao) return;

    const carregar = async () => {
      setCarregando(true);
      setErro("");
      try {
        const docSnap = await getDoc(doc(db, COLLECTION_CARTOES, id));
        if (docSnap.exists()) {
          const data = docSnap.data();
          setForm({
            empresaId: data.empresaId || "",
            nomeCartao: data.nomeCartao || "",
            bandeira: data.bandeira || "",
            bancoEmissor: data.bancoEmissor || "",
            finalCartao: data.finalCartao || "",
            limiteTotal: String(data.limiteTotal ?? ""),
            limiteDisponivel: String(data.limiteDisponivel ?? ""),
            diaFechamento: String(data.diaFechamento ?? ""),
            diaVencimento: String(data.diaVencimento ?? ""),
            contaPagamento: data.contaPagamento || "",
            status: data.status || "ATIVO",
          });
          setSucesso("");
        } else {
          setErro("Cartão não encontrado.");
        }
      } catch (e) {
        console.error("Erro ao carregar cartão:", e);
        setErro("Erro ao carregar cartão.");
      } finally {
        setCarregando(false);
      }
    };

    carregar();
  }, [id, isEdicao]);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSalvar = async (e) => {
    e.preventDefault();
    setErro("");
    setSucesso("");

    if (!usuario?.grupoId) {
      setErro("Usuário sem grupoId. Faça login novamente.");
      return;
    }

    const empresaId = restringirEmpresa ? empresaEscopoId : form.empresaId;
    if (!empresaId) {
      setErro("Selecione a empresa.");
      return;
    }

    if (!form.nomeCartao.trim()) {
      setErro("Informe o nome do cartão.");
      return;
    }

    const cliente = clientes.find((c) => String(c.id) === String(empresaId));
    const payload = {
      grupoId: usuario.grupoId,
      empresaId,
      clienteNome: cliente?.nome || cliente?.razaoSocial || "",
      nomeCartao: form.nomeCartao.trim(),
      bandeira: form.bandeira.trim(),
      bancoEmissor: form.bancoEmissor.trim(),
      finalCartao: form.finalCartao.trim(),
      limiteTotal: toNumber(form.limiteTotal),
      limiteDisponivel: toNumber(form.limiteDisponivel),
      diaFechamento: Number(form.diaFechamento || 0),
      diaVencimento: Number(form.diaVencimento || 0),
      contaPagamento: form.contaPagamento.trim(),
      status: form.status || "ATIVO",
      updatedAt: serverTimestamp(),
    };

    setSalvando(true);
    try {
      if (isEdicao) {
        await updateDoc(doc(db, COLLECTION_CARTOES, id), payload);
        setSucesso("Cartão atualizado com sucesso.");
      } else {
        await addDoc(collection(db, COLLECTION_CARTOES), {
          ...payload,
          createdAt: serverTimestamp(),
          criadoPor: usuario?.uid || usuario?.id || "",
        });
        setSucesso("Cartão criado com sucesso.");
      }
      setTimeout(() => {
        navigate("/financeiro/cartoes-credito");
      }, 500);
    } catch (e2) {
      console.error("Erro ao salvar cartão:", e2);
      setErro("Erro ao salvar cartão.");
    } finally {
      setSalvando(false);
    }
  };

  return (
    <LayoutDesktop tema={tema} titulo={isEdicao ? "Editar cartão" : "Novo cartão"} subtitulo="Financeiro / Cartões de Crédito">
      <section className="pc-card">
        {erro && <div className="pc-alert pc-alert-erro">{erro}</div>}
        {sucesso && <div className="pc-alert pc-alert-sucesso">{sucesso}</div>}

        {carregando ? (
          <div style={{ padding: 32, textAlign: "center" }}>Carregando...</div>
        ) : (
          <form onSubmit={handleSalvar} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, padding: "16px" }}>
            {/* Empresa */}
            {!restringirEmpresa && (
              <label className="pc-form-group">
                Empresa
                <select
                  value={form.empresaId}
                  onChange={(e) => setField("empresaId", e.target.value)}
                  className="pc-input"
                  disabled={salvando}
                >
                  <option value="">Selecione a empresa</option>
                  {clientes.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nome || c.razaoSocial || c.id}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {/* Nome do Cartão */}
            <label className="pc-form-group">
              Nome do cartão
              <input
                type="text"
                value={form.nomeCartao}
                onChange={(e) => setField("nomeCartao", e.target.value)}
                className="pc-input"
                disabled={salvando}
                placeholder="Ex: Bradesco Prata"
              />
            </label>

            {/* Bandeira */}
            <label className="pc-form-group">
              Bandeira
              <input
                type="text"
                value={form.bandeira}
                onChange={(e) => setField("bandeira", e.target.value)}
                className="pc-input"
                disabled={salvando}
                placeholder="Ex: Visa, Mastercard"
              />
            </label>

            {/* Banco Emissor */}
            <label className="pc-form-group">
              Banco emissor
              <input
                type="text"
                value={form.bancoEmissor}
                onChange={(e) => setField("bancoEmissor", e.target.value)}
                className="pc-input"
                disabled={salvando}
                placeholder="Ex: Bradesco, Itaú"
              />
            </label>

            {/* Final do Cartão */}
            <label className="pc-form-group">
              Final do cartão
              <input
                type="text"
                value={form.finalCartao}
                onChange={(e) => setField("finalCartao", e.target.value)}
                className="pc-input"
                disabled={salvando}
                placeholder="Ex: 1234"
              />
            </label>

            {/* Limite Total */}
            <label className="pc-form-group">
              Limite total (R$)
              <input
                type="text"
                value={form.limiteTotal}
                onChange={(e) => setField("limiteTotal", e.target.value)}
                className="pc-input"
                disabled={salvando}
                placeholder="0,00"
              />
            </label>

            {/* Limite Disponível */}
            <label className="pc-form-group">
              Limite disponível (R$)
              <input
                type="text"
                value={form.limiteDisponivel}
                onChange={(e) => setField("limiteDisponivel", e.target.value)}
                className="pc-input"
                disabled={salvando}
                placeholder="0,00"
              />
            </label>

            {/* Dia Fechamento */}
            <label className="pc-form-group">
              Dia do fechamento
              <input
                type="number"
                min="1"
                max="31"
                value={form.diaFechamento}
                onChange={(e) => setField("diaFechamento", e.target.value)}
                className="pc-input"
                disabled={salvando}
                placeholder="Ex: 10"
              />
            </label>

            {/* Dia Vencimento */}
            <label className="pc-form-group">
              Dia do vencimento
              <input
                type="number"
                min="1"
                max="31"
                value={form.diaVencimento}
                onChange={(e) => setField("diaVencimento", e.target.value)}
                className="pc-input"
                disabled={salvando}
                placeholder="Ex: 20"
              />
            </label>

            {/* Conta de Pagamento */}
            <label className="pc-form-group pc-form-group-full">
              Conta para pagamento
              <input
                type="text"
                value={form.contaPagamento}
                onChange={(e) => setField("contaPagamento", e.target.value)}
                className="pc-input"
                disabled={salvando}
                placeholder="Ex: CC001, CC002"
              />
            </label>

            {/* Status */}
            <label className="pc-form-group">
              Status
              <select
                value={form.status}
                onChange={(e) => setField("status", e.target.value)}
                className="pc-input"
                disabled={salvando}
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>

            {/* Ações */}
            <div className="pc-form-actions" style={{ gridColumn: "1 / -1" }}>
              <button
                type="button"
                className="pc-btn-secundario"
                onClick={() => navigate("/financeiro/cartoes-credito")}
                disabled={salvando}
              >
                Voltar
              </button>
              <button type="submit" className="pc-btn-primario" disabled={salvando || carregando}>
                {salvando ? "Salvando..." : isEdicao ? "Salvar alterações" : "Salvar cartão"}
              </button>
            </div>
          </form>
        )}
      </section>
    </LayoutDesktop>
  );
}
